import React, { useState } from "react";
import {
  ClassicLadderTournament,
  ClassicLadderMatch,
} from "../types/tournament";
import {
  updateClassicLadderMatch,
  getCurrentClassicLadderMatches,
  getClassicLadderProgress,
  createNextClassicLadderRound,
  getLadderStandings,
  calculateClassicLadderStats,
} from "../utils/classicLadderLogic";
import ClassicLadderDisplay from "./ClassicLadderDisplay";
import ClassicLadderScoreEntry from "./ClassicLadderScoreEntry";
import { RotateCcw, Trophy, Users, BarChart3, Play, Crown } from "lucide-react";

interface ClassicLadderDashboardProps {
  tournament: ClassicLadderTournament;
  setTournament: (tournament: ClassicLadderTournament) => void;
  onReset: () => void;
}

const ClassicLadderDashboard: React.FC<ClassicLadderDashboardProps> = ({
  tournament,
  setTournament,
  onReset,
}) => {
  const [selectedMatch, setSelectedMatch] = useState<ClassicLadderMatch | null>(
    null
  );
  const [activeView, setActiveView] = useState<"ladder" | "current" | "stats">(
    "ladder"
  );

  const currentMatches = getCurrentClassicLadderMatches(tournament);
  const progress = getClassicLadderProgress(tournament);
  const standings = getLadderStandings(tournament);
  const stats = calculateClassicLadderStats(tournament);

  const handleMatchClick = (match: ClassicLadderMatch) => {
    if (!match.isCompleted) {
      setSelectedMatch(match);
    }
  };

  const handleScoreSubmit = (
    matchId: string,
    team1Score: number,
    team2Score: number
  ) => {
    const updatedTournament = updateClassicLadderMatch(
      tournament,
      matchId,
      team1Score,
      team2Score
    );
    setTournament(updatedTournament);
  };

  const handleNextRound = () => {
    const updatedTournament = createNextClassicLadderRound(tournament);
    setTournament(updatedTournament);
  };

  const currentRound = tournament.rounds.find(
    (round) => round.roundNumber === tournament.currentRound
  );
  const isRoundComplete = currentRound?.isCompleted || false;
  const canAdvanceRound =
    isRoundComplete && tournament.currentRound < tournament.totalRounds;
  const isTournamentComplete =
    tournament.currentRound === tournament.totalRounds && isRoundComplete;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                🏆 {tournament.name}
              </h1>
              <p className="text-sm text-gray-600">
                Classic Speed Ladder • {tournament.teams.length} Teams • Round{" "}
                {tournament.currentRound} of {tournament.totalRounds}
              </p>
              {tournament.court1Defender && (
                <p className="text-sm text-blue-600 font-medium">
                  👑 Court 1 Defender: {tournament.court1Defender.name} (
                  {tournament.court1DefenderStreak} round
                  {tournament.court1DefenderStreak !== 1 ? "s" : ""})
                </p>
              )}
            </div>

            <div className="flex items-center space-x-4">
              {canAdvanceRound && (
                <button
                  onClick={handleNextRound}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center space-x-2"
                >
                  <Play className="w-4 h-4" />
                  <span>Next Round</span>
                </button>
              )}

              <button
                onClick={onReset}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center space-x-2"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Reset</span>
              </button>
            </div>
          </div>

          {/* Navigation */}
          <div className="bg-white rounded-lg shadow-lg">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-4 px-6 overflow-x-auto">
                <button
                  onClick={() => setActiveView("ladder")}
                  className={`py-4 px-2 border-b-2 font-medium text-sm flex items-center whitespace-nowrap ${
                    activeView === "ladder"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Ladder
                </button>
                <button
                  onClick={() => setActiveView("current")}
                  className={`py-4 px-2 border-b-2 font-medium text-sm flex items-center whitespace-nowrap ${
                    activeView === "current"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Current Matches
                </button>
                <button
                  onClick={() => setActiveView("stats")}
                  className={`py-4 px-2 border-b-2 font-medium text-sm flex items-center whitespace-nowrap ${
                    activeView === "stats"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Statistics
                </button>
              </nav>
            </div>

            {/* Content */}
            <div className="p-6">
              {activeView === "ladder" && (
                <ClassicLadderDisplay
                  tournament={tournament}
                  standings={standings}
                  onMatchClick={handleMatchClick}
                />
              )}

              {activeView === "current" && (
                <div className="space-y-6">
                  {/* Tournament Progress */}
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Tournament Progress
                    </h3>
                    <div className="flex items-center space-x-4">
                      <div className="flex-1">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress.progress}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        Round {tournament.currentRound} of{" "}
                        {tournament.totalRounds}
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-gray-600">
                      {progress.completedMatches} of {progress.totalMatches}{" "}
                      total matches completed
                    </div>
                  </div>

                  {/* Current Matches */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Current Round Matches
                    </h3>
                    {currentMatches.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {currentMatches.map((match) => (
                          <div
                            key={match.id}
                            className={`bg-white rounded-lg shadow-lg p-6 border-2 cursor-pointer hover:shadow-xl transition-all duration-200 ${
                              match.court === 1
                                ? "border-yellow-400 bg-gradient-to-br from-yellow-50 to-amber-50"
                                : "border-blue-300"
                            }`}
                            onClick={() => handleMatchClick(match)}
                          >
                            <div className="text-center mb-4">
                              <h4
                                className={`font-semibold ${
                                  match.court === 1
                                    ? "text-yellow-700"
                                    : "text-gray-900"
                                }`}
                              >
                                Court {match.court} {match.court === 1 && "👑"}
                              </h4>
                              <p className="text-sm text-gray-600">
                                Round {match.round}
                              </p>
                            </div>

                            <div className="space-y-3">
                              <div className="text-center">
                                <div className="font-semibold text-gray-900">
                                  {match.team1.name}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {match.team1.player1} & {match.team1.player2}
                                </div>
                                <div className="text-xs text-blue-600">
                                  {match.team1.wins}W - {match.team1.losses}L
                                </div>
                              </div>

                              <div className="text-center text-gray-500 font-medium">
                                VS
                              </div>

                              <div className="text-center">
                                <div className="font-semibold text-gray-900">
                                  {match.team2.name}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {match.team2.player1} & {match.team2.player2}
                                </div>
                                <div className="text-xs text-blue-600">
                                  {match.team2.wins}W - {match.team2.losses}L
                                </div>
                              </div>
                            </div>

                            <div className="mt-4 text-center">
                              <span
                                className={`px-3 py-1 rounded-full text-sm font-medium ${
                                  match.court === 1
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-blue-100 text-blue-800"
                                }`}
                              >
                                {match.court === 1
                                  ? "Court 1 Battle!"
                                  : "Ready to Play"}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 text-lg">
                          {isTournamentComplete
                            ? "Tournament Complete!"
                            : isRoundComplete
                            ? "Round Complete! Ready for next round."
                            : "No matches in current round"}
                        </p>
                        {isTournamentComplete && tournament.champion && (
                          <div className="mt-4">
                            <p className="text-lg font-semibold text-green-600">
                              🏆 Champion: {tournament.champion.name}
                            </p>
                            <p className="text-sm text-gray-600">
                              {tournament.champion.player1} &{" "}
                              {tournament.champion.player2}
                            </p>
                            <p className="text-sm text-blue-600 mt-2">
                              Final Position: Court 1 •{" "}
                              {tournament.champion.wins}W -{" "}
                              {tournament.champion.losses}L
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeView === "stats" && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-white rounded-lg shadow-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        Tournament Info
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div>
                          <strong>Teams:</strong> {tournament.teams.length}
                        </div>
                        <div>
                          <strong>Courts:</strong> {tournament.courts.length}
                        </div>
                        <div>
                          <strong>Current Round:</strong>{" "}
                          {tournament.currentRound} of {tournament.totalRounds}
                        </div>
                        <div>
                          <strong>Progress:</strong>{" "}
                          {Math.round(progress.progress)}%
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        Most Wins
                      </h4>
                      <div className="font-semibold text-green-600">
                        {stats.mostWins.name}
                      </div>
                      <div className="text-sm text-gray-600">
                        {stats.mostWins.player1} & {stats.mostWins.player2}
                      </div>
                      <div className="text-sm text-blue-600 mt-1">
                        {stats.mostWins.wins}W - {stats.mostWins.losses}L •
                        Court {stats.mostWins.currentCourt}
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        Biggest Climber
                      </h4>
                      <div className="font-semibold text-blue-600">
                        {stats.biggestClimber.name}
                      </div>
                      <div className="text-sm text-gray-600">
                        {stats.biggestClimber.player1} &{" "}
                        {stats.biggestClimber.player2}
                      </div>
                      <div className="text-sm text-green-600 mt-1">
                        Climbed{" "}
                        {stats.biggestClimber.startingCourt -
                          stats.biggestClimber.currentCourt}{" "}
                        courts (from Court {stats.biggestClimber.startingCourt}{" "}
                        → Court {stats.biggestClimber.currentCourt})
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        Court 1 Defender
                      </h4>
                      {tournament.court1Defender ? (
                        <div>
                          <div className="font-semibold text-yellow-600">
                            {tournament.court1Defender.name}
                          </div>
                          <div className="text-sm text-gray-600">
                            {tournament.court1Defender.player1} &{" "}
                            {tournament.court1Defender.player2}
                          </div>
                          <div className="text-sm text-yellow-600 mt-1">
                            👑 {tournament.court1DefenderStreak} round
                            {tournament.court1DefenderStreak !== 1
                              ? "s"
                              : ""}{" "}
                            defending
                          </div>
                        </div>
                      ) : (
                        <div className="text-gray-500">No current defender</div>
                      )}
                    </div>

                    <div className="bg-white rounded-lg shadow-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        Most Active
                      </h4>
                      <div className="font-semibold text-purple-600">
                        {stats.mostActive.name}
                      </div>
                      <div className="text-sm text-gray-600">
                        {stats.mostActive.player1} & {stats.mostActive.player2}
                      </div>
                      <div className="text-sm text-purple-600 mt-1">
                        {stats.mostActive.totalMovement} total court movements
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        Tournament Stats
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div>
                          <strong>Total Games:</strong>{" "}
                          {Math.round(stats.totalGamesPlayed)}
                        </div>
                        <div>
                          <strong>Avg Movement:</strong>{" "}
                          {stats.averageMovement.toFixed(1)} courts
                        </div>
                        <div>
                          <strong>Court 1 Veterans:</strong>{" "}
                          {stats.court1History.length} teams
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Court 1 History */}
                  {stats.court1History.length > 0 && (
                    <div className="bg-white rounded-lg shadow-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-4">
                        Court 1 Veterans
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {stats.court1History.map((team) => (
                          <div
                            key={team.id}
                            className="bg-yellow-50 border border-yellow-200 rounded-lg p-3"
                          >
                            <div className="font-semibold text-yellow-700">
                              {team.name}
                            </div>
                            <div className="text-sm text-gray-600">
                              {team.player1} & {team.player2}
                            </div>
                            <div className="text-xs text-yellow-600 mt-1">
                              Has won on Court 1 👑
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Score Entry Modal */}
      {selectedMatch && (
        <ClassicLadderScoreEntry
          match={selectedMatch}
          onClose={() => setSelectedMatch(null)}
          onSubmit={handleScoreSubmit}
        />
      )}
    </div>
  );
};

export default ClassicLadderDashboard;
